import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DATA_API_URL, CAM_API_URL, TOKEN } from '../config';

interface SettingsProps {
  open: boolean;
  onClose: () => void;
}

interface SettingsData {
  dataApiUrl: string;
  camApiUrl: string;
  camApiKey: string;
}

const Settings: React.FC<SettingsProps> = ({ open, onClose }) => {
  const [settings, setSettings] = useState<SettingsData>({
    dataApiUrl: '',
    camApiUrl: '',
    camApiKey: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Load settings from localStorage on component mount
  useEffect(() => {
    const storedDataApiUrl = localStorage.getItem('dataApiUrl');
    const storedCamApiUrl = localStorage.getItem('camApiUrl');
    const storedCamApiKey = localStorage.getItem('camApiKey');

    setSettings({
      dataApiUrl: storedDataApiUrl || DATA_API_URL,
      camApiUrl: storedCamApiUrl || CAM_API_URL,
      camApiKey: storedCamApiKey || TOKEN
    });
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    // Save to localStorage
    localStorage.setItem('dataApiUrl', settings.dataApiUrl);
    localStorage.setItem('camApiUrl', settings.camApiUrl);
    localStorage.setItem('camApiKey', settings.camApiKey);

    // Show success message
    setSnackbarOpen(true);

    // Close the dialog after a delay
    setTimeout(() => {
      onClose();
      // Reload the page to apply new settings
      window.location.reload();
    }, 1500);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Application Settings
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Configure API endpoints and authentication. These settings will be saved in your browser.
            </Typography>
            
            <TextField
              name="dataApiUrl"
              label="Data API URL"
              value={settings.dataApiUrl}
              onChange={handleChange}
              fullWidth
              margin="normal"
              variant="outlined"
              helperText="Backend server URL (e.g., http://localhost:8000)"
            />
            
            <TextField
              name="camApiUrl"
              label="Camera API URL"
              value={settings.camApiUrl}
              onChange={handleChange}
              fullWidth
              margin="normal"
              variant="outlined"
              helperText="Camera server URL (e.g., http://localhost:8080)"
            />
            
            <TextField
              name="camApiKey"
              label="Camera API Key"
              value={settings.camApiKey}
              onChange={handleChange}
              fullWidth
              margin="normal"
              variant="outlined"
              type="password"
              helperText="API key for camera access"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default Settings; 